import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { API_URL } from '../constants/appConstants';
import { TaskInquiry } from '../types';

// Notification types
export type NotificationType = 'task_added' | 'task_removed' | 'task_updated' | 'info' | 'success' | 'warning' | 'error' | 'task_inquiry';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  taskId?: string;
  taskName?: string;
  // Task inquiry specific fields
  inquiryId?: number;
  fromEmployeeName?: string;
  taskProject?: string;
  taskTimeSeconds?: number;
  inquiryType?: string;
}

export interface Toast {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  duration?: number; // ms, default 4000
}

interface NotificationContextType {
  notifications: Notification[];
  toasts: Toast[];
  unreadCount: number;
  taskInquiries: TaskInquiry[];
  taskInquiryCount: number;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  showToast: (toast: Omit<Toast, 'id'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  dismissToast: (id: string) => void;
  fetchTaskInquiries: (employeeId: string) => Promise<void>;
  markInquiryAsRead: (inquiryId: number) => Promise<void>;
  respondToInquiry: (inquiryId: number, response: string) => Promise<void>;
  setCurrentEmployeeId: (employeeId: string | null) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

let notificationIdCounter = 0;
let toastIdCounter = 0;

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [taskInquiries, setTaskInquiries] = useState<TaskInquiry[]>([]);
  const [currentEmployeeId, setCurrentEmployeeId] = useState<string | null>(null);

  // Fetch task inquiries for the current user
  const fetchTaskInquiries = useCallback(async (employeeId: string) => {
    try {
      const response = await axios.get<TaskInquiry[]>(
        `${API_URL}/task-inquiries/my?employeeId=${employeeId}`,
        { withCredentials: true }
      );
      setTaskInquiries(response.data || []);
      
      // Add unread inquiries as notifications
      const unreadInquiries = (response.data || []).filter(i => i.status === 'unread');
      unreadInquiries.forEach(inquiry => {
        // Check if we already have this notification
        const existingNotifId = `inquiry-${inquiry.id}`;
        setNotifications(prev => {
          if (prev.some(n => n.id === existingNotifId)) {
            return prev;
          }
          return [{
            id: existingNotifId,
            type: 'task_inquiry' as NotificationType,
            title: `Question from ${inquiry.from_employee_name || 'Manager'}`,
            message: inquiry.message,
            timestamp: new Date(inquiry.created_at),
            read: false,
            inquiryId: inquiry.id,
            fromEmployeeName: inquiry.from_employee_name || undefined,
            taskId: inquiry.intranet_task_id || undefined,
            taskName: inquiry.task_description || undefined,
            taskProject: inquiry.task_project || undefined,
            taskTimeSeconds: inquiry.task_time_seconds || undefined,
            inquiryType: inquiry.inquiry_type
          }, ...prev].slice(0, 50);
        });
      });
    } catch (err) {
      console.debug('Could not fetch task inquiries:', err);
    }
  }, []);

  // Mark a task inquiry as read
  const markInquiryAsRead = useCallback(async (inquiryId: number) => {
    try {
      await axios.put(`${API_URL}/task-inquiries/${inquiryId}/read`, {}, { withCredentials: true });
      setTaskInquiries(prev => 
        prev.map(i => i.id === inquiryId ? { ...i, status: 'read' as const } : i)
      );
      // Also mark the corresponding notification as read
      setNotifications(prev =>
        prev.map(n => n.inquiryId === inquiryId ? { ...n, read: true } : n)
      );
    } catch (err) {
      console.error('Failed to mark inquiry as read:', err);
    }
  }, []);

  // Respond to a task inquiry
  const respondToInquiry = useCallback(async (inquiryId: number, response: string) => {
    try {
      await axios.put(
        `${API_URL}/task-inquiries/${inquiryId}/respond`,
        { response },
        { withCredentials: true }
      );
      setTaskInquiries(prev =>
        prev.map(i => i.id === inquiryId ? { ...i, status: 'responded' as const, response } : i)
      );
      // Mark notification as read
      setNotifications(prev =>
        prev.map(n => n.inquiryId === inquiryId ? { ...n, read: true } : n)
      );
    } catch (err) {
      console.error('Failed to respond to inquiry:', err);
      throw err;
    }
  }, []);

  // Poll for new inquiries periodically when we have an employee ID
  useEffect(() => {
    if (!currentEmployeeId) return;
    
    // Fetch immediately
    fetchTaskInquiries(currentEmployeeId);
    
    // Then poll every 2 minutes
    const interval = setInterval(() => {
      fetchTaskInquiries(currentEmployeeId);
    }, 2 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [currentEmployeeId, fetchTaskInquiries]);

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: `notif-${++notificationIdCounter}`,
      timestamp: new Date(),
      read: false,
    };
    
    setNotifications(prev => [newNotification, ...prev].slice(0, 50)); // Keep max 50 notifications
  }, []);

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${++toastIdCounter}`;
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration ?? 4000,
    };
    
    setToasts(prev => [...prev, newToast]);
    
    // Auto-dismiss after duration
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, newToast.duration);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const markAsRead = useCallback((id: string) => {
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;
  const taskInquiryCount = taskInquiries.filter(i => i.status === 'unread').length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        toasts,
        unreadCount,
        taskInquiries,
        taskInquiryCount,
        addNotification,
        showToast,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearAllNotifications,
        dismissToast,
        fetchTaskInquiries,
        markInquiryAsRead,
        respondToInquiry,
        setCurrentEmployeeId,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Helper functions for common notification types
export const useTaskNotifications = () => {
  const { addNotification, showToast } = useNotifications();

  const notifyTaskAdded = useCallback((taskName: string, taskId?: string) => {
    addNotification({
      type: 'task_added',
      title: 'Task Added',
      message: `"${taskName}" has been added to your Tasks for Today`,
      taskId,
      taskName,
    });
    showToast({
      type: 'task_added',
      title: 'Task Added',
      message: taskName,
    });
  }, [addNotification, showToast]);

  const notifyTaskRemoved = useCallback((taskName: string, taskId?: string) => {
    addNotification({
      type: 'task_removed',
      title: 'Task Removed',
      message: `"${taskName}" has been removed from your Tasks for Today`,
      taskId,
      taskName,
    });
    showToast({
      type: 'task_removed',
      title: 'Task Removed',
      message: taskName,
    });
  }, [addNotification, showToast]);

  const notifyTaskUpdated = useCallback((taskName: string, taskId?: string) => {
    addNotification({
      type: 'task_updated',
      title: 'Task Updated',
      message: `"${taskName}" has been updated`,
      taskId,
      taskName,
    });
    showToast({
      type: 'task_updated',
      title: 'Task Updated',
      message: taskName,
    });
  }, [addNotification, showToast]);

  return { notifyTaskAdded, notifyTaskRemoved, notifyTaskUpdated };
};

